import { IonImg, IonToast } from '@ionic/react'
import React, { useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { logEvent } from '../../../analytics/utils/firebaseUtils'
import Button from '../../../components/actions/Button'
import useUser from '../../../contexts/hooks/useUser'
import { ModalContext } from '../../../contexts/ModalContext'
import { getLinkEnodeData } from '../../../modals/eNodeInit/components/getLinkEnodeData'
import { useLinkEnode } from '../../../modals/eNodeInit/hooks/useLinkEnode'
import { trpc, trpcProxyClient } from '../../../modules/trpc'

interface AddEnodeDeviceProps {
	goForward: () => void
	type: 'vehicle' | 'inverter' | 'hvac'
}

const AddEnodeDevice: React.FC<AddEnodeDeviceProps> = ({ goForward, type }) => {
	const utils = trpc.useContext()

	const { insertModal } = useContext(ModalContext)

	const { t } = useTranslation('pages_enodeInit', { keyPrefix: 'linkEnode' })

	const { user, setUser } = useUser()
	const linkEnode = useLinkEnode(goForward, type)

	const data = getLinkEnodeData({ type })

	const [toastOpen, setToastOpen] = useState(false)
	const [toastMsg, setToastMsg] = useState('')

	const handleCheckForCarLink = async () => {
		logEvent('click-ev-check-is-linked')
		if (!user?.id) return

		const isLinked = await trpcProxyClient.eNode.vehicles.checkForCarLink.query({
			hashedPid: user?.hashedPid ?? '',
			userId: user?.id ?? 0,
		})

		if (isLinked) {
			setUser({
				vehicleLinked: true,
			})
			setToastMsg(t('autoLoytyi'))
			setToastOpen(true)
			utils.objects.invalidate()
			utils.eNode.invalidate()
			return
		}

		setToastMsg(t('autoaEiLoytynyt'))
		setToastOpen(true)
	}

	const imageBackground =
		type === 'vehicle' ? 'bg-[#defae4]' : type === 'inverter' ? 'bg-[#e1ecfc]' : 'bg-[#f2f0d5]'

	return (
		<div
			className='flex flex-col flex-1 h-full min-h-0 overflow-y-auto bg-[#f5f5f5] dark:bg-background-100-dark'
			id='new-smart-device'
		>
			<div className='flex flex-col flex-1 justify-between h-full'>
				<div>
					<div className={'p-8 rounded-3xl ' + imageBackground}>
						<IonImg className='h-[180px]' src={data.image} />
					</div>

					<h1 className='my-4 text-text-dark dark:text-white'>
						{type === 'vehicle'
							? t('otsikko2Auto')
							: type === 'inverter'
								? t('otsikko2Paneeli')
								: t('otsikko2Hvac')}
					</h1>

					<div className='description my-2 text-text-dark dark:text-white'>
						<p>{data.description.text1}</p>
					</div>

					{data.description?.text2 && (
						<div className='description my-4 text-text-dark dark:text-white'>
							<p>{data.description.text2}</p>
						</div>
					)}
					{data.description?.compatibility && (
						<div className='description my-4 text-text-grey-200 dark:text-text-grey-100'>
							<p>
								{data.description.compatibility}

								<a href={data.link} target='_blank' rel='noreferrer'>
									<span className='text-highlight'> {t('taalta')}</span>
								</a>
							</p>
						</div>
					)}
				</div>

				<div className='flex flex-col items-center gap-1 h-fit pb-4'>
					<Button className='m-0' onClick={linkEnode.link}>
						{data.buttonText}
					</Button>
					{type === 'vehicle' && (
						<>
							<Button
								className='hollow m-0'
								onClick={() => insertModal('smartChargingInstructions')}
							>
								{t('lisatietoja')}
							</Button>
							<Button className='hollow m-0' onClick={handleCheckForCarLink}>
								{t('tarkistaAutonLisays')}
							</Button>
						</>
					)}
				</div>
			</div>

			<IonToast
				onDidDismiss={() => setToastOpen(false)}
				duration={5000}
				isOpen={toastOpen}
				message={toastMsg}
			/>
		</div>
	)
}

export default AddEnodeDevice
